import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { SubscriptionPlanTypes } from '../../types/coach';
import StyledButton from '../Common/StyledButton';
import { formatPlanName, formatPrice } from '../../utils/paymentUtills';

type IPlanProps = {
  planIcon: string;
  planType: SubscriptionPlanTypes;
  price: number;
  features: string[];
  handleSelectPlan: (_planType: SubscriptionPlanTypes) => void;
  showMoreInfo: (_planType: SubscriptionPlanTypes) => void;
  xs?: number;
  loading?: boolean;
  sm?: number;
  md?: number;
  lg?: number;
};

function Plan({
  planIcon,
  planType,
  price,
  features,
  handleSelectPlan,
  showMoreInfo,
  xs = 4,
  loading,
  sm,
  md,
  lg,
}: IPlanProps) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const { pkgPrice, period } = formatPrice(price, planType);

  return (
    <Grid
      item
      xs={xs}
      sm={sm}
      md={md}
      lg={lg}
      px={3}
      pt={4}
      pb={3.5}
      width="100%"
      display="flex"
      flexDirection="column"
      alignItems={{
        xs: 'flex-start',
        sm: 'center',
      }}
      borderRadius="10px"
      bgcolor="#fff"
      boxShadow={theme.borderStyles.cardBorders}
      gap={{
        xs: '16px',
        sm: '25px',
      }}
    >
      <Box
        alignItems={{
          xs: 'flex-start',
          sm: 'center',
        }}
        gap={{
          xs: '8px',
          sm: '16px',
        }}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Box
          display="flex"
          width="100%"
          gap="12px"
          alignItems="center"
          flexDirection={{ xs: 'row-reverse', sm: 'column' }}
          justifyContent="space-between"
        >
          <img src={planIcon} alt={planType} width={36} height={36} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <Typography variant="bodyBold" color="primary">
              {formatPlanName(planType)}
            </Typography>

            <Box onClick={() => showMoreInfo(planType)}>
              <img
                src="/assets/icons/question-circle.svg"
                alt="question-circle"
                style={{ cursor: 'pointer' }}
              />
            </Box>
          </Box>
        </Box>
        <Box display="flex" alignItems="baseline">
          <Typography variant="title2Bold" color="primary" fontWeight="bold" textAlign="center">
            {pkgPrice}
          </Typography>
          {period && (
            <Typography variant="meta" color="primary" sx={{ ml: 0.5 }}>
              {period}
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 1,
        }}
      >
        {features.map((feature) => (
          <Typography
            key={feature}
            variant="calloutRegular"
            fontWeight={400}
            component="div"
            sx={{ display: 'flex', alignItems: 'flex-start', color: 'primary' }}
          >
            <Box
              component="span"
              sx={{
                width: '1rem',
                display: 'flex',
                justifyContent: 'flex-start',
              }}
            >
              &#8226;
            </Box>
            <Box
              component="span"
              sx={{
                flex: 1,
                wordBreak: 'break-word',
              }}
            >
              {t(`subscriptionPlans.features.${feature}`)}
            </Box>
          </Typography>
        ))}
      </Box>

      <Box mt="auto" sx={{ width: '100%', textAlign: 'center' }}>
        <StyledButton
          fullWidth={isSmallScreen}
          paddingX={27}
          paddingY={12}
          onClick={() => handleSelectPlan(planType)}
          loading={loading}
          loadingText={t('therapistOnboarding.selectSubscriptionPlan.selectingPlan')}
        >
          {t('therapistOnboarding.selectSubscriptionPlan.selectPlan')}
        </StyledButton>
      </Box>
    </Grid>
  );
}

export default Plan;
